import { useState } from "react";

import { makePostApiCall, makePutApiCall } from "../../../shared/makeApiCall";
import LocationZoneForm from "../../../components/forms/setup/location/LocationZoneForm";
import LocationProductionRoomForm from "../../../components/forms/setup/location/LocationProductionRoomForm";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import { generateUniqueNumber } from "../../../shared/utility";

const emptyPaginated = { data: [], total: 0 };

function syncDefaultLocation(
  onDefaultLocationChange,
  values,
  locationRecord,
  savedLocation,
) {
  if (!onDefaultLocationChange || !("is_default" in values)) {
    return;
  }

  if (values.is_default) {
    onDefaultLocationChange({
      value: savedLocation?.id ?? locationRecord?.id,
      label: savedLocation?.name ?? values.name,
    });
    return;
  }

  const wasDefault =
    locationRecord?.is_default === 1 || locationRecord?.is_default === true;
  if (wasDefault) {
    onDefaultLocationChange(null);
  }
}

export function LocationDetailsViewModel(
  logout,
  addSnackbar,
  onDefaultLocationChange,
) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const [zones, setZones] = useState(emptyPaginated);
  const [rooms, setRooms] = useState(emptyPaginated);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const handleFailure = (callResult, title, fallback) => {
    if (callResult.errorStatus === 401) {
      logout();
      return;
    }
    showErrorModal(
      title,
      callResult.data?.message || callResult.data?.subtitle || fallback,
    );
  };

  const loadLocation = async (locationId) => {
    setLoading(true);
    const callResult = await makePostApiCall(`locations/${locationId}`, {});
    setLoading(false);

    if (callResult.success) {
      setLocation(callResult.data.location);
      return callResult.data.location;
    }

    handleFailure(callResult, "Could not load location", "Please try again.");
    return null;
  };

  const getZones = async (locationRecord, search, page) => {
    if (!locationRecord?.id) {
      setZones(emptyPaginated);
      return;
    }

    setZonesLoading(true);
    const callResult = await makePostApiCall("locationZones", {
      location: { value: locationRecord.id, label: locationRecord.name },
      page,
      search: search ?? "",
    });
    setZonesLoading(false);

    if (callResult.success) {
      setZones(callResult.data);
      return;
    }

    handleFailure(callResult, "Could not load zones", "Please try again.");
  };

  const getRooms = async (locationRecord, search, page) => {
    if (!locationRecord?.id) {
      setRooms(emptyPaginated);
      return;
    }

    setRoomsLoading(true);
    const callResult = await makePostApiCall("locationProductionRooms", {
      location: { value: locationRecord.id, label: locationRecord.name },
      page,
      search: search ?? "",
    });
    setRoomsLoading(false);

    if (callResult.success) {
      setRooms(callResult.data);
      return;
    }

    handleFailure(callResult, "Could not load rooms", "Please try again.");
  };

  const initLocation = async (locationId) => {
    const loaded = await loadLocation(locationId);
    if (loaded) {
      await Promise.all([getZones(loaded, "", 1), getRooms(loaded, "", 1)]);
    }
  };

  const createLocation = async (values) => {
    setSaving(true);
    const callResult = await makePostApiCall("createLocation", values);
    setSaving(false);

    if (callResult.success) {
      const created = callResult.data.location;
      syncDefaultLocation(onDefaultLocationChange, values, null, created);
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Location created",
          SnackbarType.Success,
        ),
      );
      return created;
    }

    handleFailure(callResult, "Create failed", "Could not create location.");
    return null;
  };

  const updateLocation = async (values, locationRecord) => {
    setSaving(true);
    const callResult = await makePutApiCall(
      `locations/${locationRecord.id}`,
      values,
    );
    setSaving(false);

    if (callResult.success) {
      const updated = callResult.data?.location ?? {
        ...locationRecord,
        ...values,
      };
      setLocation(updated);
      syncDefaultLocation(
        onDefaultLocationChange,
        values,
        locationRecord,
        updated,
      );
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message || "Location updated",
          SnackbarType.Success,
        ),
      );
      return true;
    }

    handleFailure(callResult, "Update failed", "Could not update location.");
    return false;
  };

  const deleteLocation = async (locationRecord, onSuccess) => {
    setSaving(true);
    const callResult = await makePostApiCall(
      `deleteLocation/${locationRecord.id}`,
      {},
    );
    setSaving(false);

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message || "Location deleted",
          SnackbarType.Success,
        ),
      );
      onSuccess?.();
      return true;
    }

    handleFailure(callResult, "Delete failed", "Could not delete location.");
    return false;
  };

  const askDeleteLocation = (locationRecord, onSuccess) => {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteLocation(locationRecord, onSuccess)}
          message={`Are you sure you want to delete ${locationRecord.name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const createZone = async (values) => {
    if (!location) {
      return;
    }

    const callResult = await makePostApiCall("createLocationZone", {
      location: { value: location.id, label: location.name },
      ...values,
    });

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Zone created",
          SnackbarType.Success,
        ),
      );
      setSlideOver({ childComponent: null, open: false });
      getZones(location, "", 1);
      return;
    }

    handleFailure(callResult, "Create failed", "Could not create zone.");
  };

  const updateZone = async (values, zone) => {
    const callResult = await makePutApiCall(`locationZones/${zone.id}`, values);

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message || "Zone updated",
          SnackbarType.Success,
        ),
      );
      setSlideOver({ childComponent: null, open: false });
      getZones(location, "", 1);
      return;
    }

    handleFailure(callResult, "Update failed", "Could not update zone.");
  };

  const deleteZone = async (zone) => {
    setZonesLoading(true);
    const callResult = await makePostApiCall(
      `deleteLocationZone/${zone.id}`,
      {},
    );
    setZonesLoading(false);

    if (callResult.success) {
      setEmptyModelOpen({ open: false, childComponent: null });
      getZones(location, "", 1);
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message || "Zone deleted",
          SnackbarType.Success,
        ),
      );
      return;
    }

    handleFailure(callResult, "Delete failed", "Could not delete zone.");
  };

  const openAddZoneSlideOver = () => {
    setSlideOver({
      childComponent: (
        <LocationZoneForm
          isEdit={false}
          setOpen={setSlideOver}
          actionCall={createZone}
        />
      ),
      open: true,
    });
  };

  const openEditZoneSlideOver = (zone) => {
    setSlideOver({
      childComponent: (
        <LocationZoneForm
          isEdit={true}
          zone={zone}
          setOpen={setSlideOver}
          actionCall={updateZone}
        />
      ),
      open: true,
    });
  };

  const askDeleteZone = (zone) => {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteZone(zone)}
          message={`Are you sure you want to delete ${zone.name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const createRoom = async (values) => {
    if (!location) {
      return;
    }

    const callResult = await makePostApiCall("createLocationProductionRoom", {
      location: { value: location.id, label: location.name },
      ...values,
    });

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Room created",
          SnackbarType.Success,
        ),
      );
      setSlideOver({ childComponent: null, open: false });
      getRooms(location, "", 1);
      return;
    }

    handleFailure(callResult, "Create failed", "Could not create room.");
  };

  const updateRoom = async (values, room) => {
    const callResult = await makePutApiCall(
      `locationProductionRooms/${room.id}`,
      values,
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message || "Room updated",
          SnackbarType.Success,
        ),
      );
      setSlideOver({ childComponent: null, open: false });
      getRooms(location, "", 1);
      return;
    }

    handleFailure(callResult, "Update failed", "Could not update room.");
  };

  const deleteRoom = async (room) => {
    setRoomsLoading(true);
    const callResult = await makePostApiCall(
      `deleteLocationProductionRoom/${room.id}`,
      {},
    );
    setRoomsLoading(false);

    if (callResult.success) {
      setEmptyModelOpen({ open: false, childComponent: null });
      getRooms(location, "", 1);
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data?.message || "Room deleted",
          SnackbarType.Success,
        ),
      );
      return;
    }

    handleFailure(callResult, "Delete failed", "Could not delete room.");
  };

  const openAddRoomSlideOver = () => {
    setSlideOver({
      childComponent: (
        <LocationProductionRoomForm
          isEdit={false}
          setOpen={setSlideOver}
          actionCall={createRoom}
        />
      ),
      open: true,
    });
  };

  const openEditRoomSlideOver = (room) => {
    setSlideOver({
      childComponent: (
        <LocationProductionRoomForm
          isEdit={true}
          room={room}
          setOpen={setSlideOver}
          actionCall={updateRoom}
        />
      ),
      open: true,
    });
  };

  const askDeleteRoom = (room) => {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteRoom(room)}
          message={`Are you sure you want to delete ${room.name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  return {
    emptyModal,
    setEmptyModelOpen,
    loading,
    saving,
    zonesLoading,
    roomsLoading,
    location,
    zones,
    rooms,
    getZones,
    getRooms,
    initLocation,
    createLocation,
    updateLocation,
    askDeleteLocation,
    slideOver,
    setSlideOver,
    openAddZoneSlideOver,
    openEditZoneSlideOver,
    askDeleteZone,
    openAddRoomSlideOver,
    openEditRoomSlideOver,
    askDeleteRoom,
  };
}
